import React, { ReactNode } from "react";
import { Avatar, Badge, Box, Flex, Text } from "@chakra-ui/react";
import { DateTime } from "luxon";

/**
 * Small presentational helpers shared by HRMS lists so names, dates, leave
 * types and statuses look the same on every page.
 */

/** Colour + short label per leave type code (matches Leave Policy cards). */
export const LEAVE_TYPE_META: Record<string, { label: string; color: string }> = {
  casual: { label: "Casual", color: "teal" },
  sick: { label: "Sick", color: "red" },
  exigency: { label: "Exigency", color: "orange" },
  optional: { label: "Optional", color: "purple" },
  compensatory: { label: "Comp Off", color: "blue" },
  unpaid: { label: "Unpaid", color: "gray" },
};

export const leaveTypeColor = (code?: string) =>
  LEAVE_TYPE_META[code || ""]?.color || "gray";

const STATUS_COLOR: Record<string, string> = {
  approved: "green",
  confirmed: "green",
  completed: "green",
  onboarded: "green",
  active: "green",
  pending: "orange",
  probation: "orange",
  submitted: "blue",
  in_progress: "blue",
  rejected: "red",
  declined: "red",
  cancelled: "gray",
  exited: "gray",
  inactive: "gray",
};

/** "18 Sep 2026" from an ISO date/datetime; "-" when empty or unparseable. */
export const formatHrmsDate = (value?: string | null) => {
  if (!value) return "-";
  const date = DateTime.fromISO(value);
  return date.isValid ? date.toFormat("dd LLL yyyy") : value;
};

/** "18 Sep 2026" for a single day, "18 – 20 Sep 2026" for a range. */
export const formatHrmsDateRange = (start?: string, end?: string) => {
  const s = start ? DateTime.fromISO(start) : null;
  const e = end ? DateTime.fromISO(end) : null;
  if (!s?.isValid) return formatHrmsDate(start);
  if (!e?.isValid || s.hasSame(e, "day")) return s.toFormat("dd LLL yyyy");
  if (s.hasSame(e, "month")) return `${s.toFormat("dd")} – ${e.toFormat("dd LLL yyyy")}`;
  if (s.hasSame(e, "year")) return `${s.toFormat("dd LLL")} – ${e.toFormat("dd LLL yyyy")}`;
  return `${s.toFormat("dd LLL yyyy")} – ${e.toFormat("dd LLL yyyy")}`;
};

/** Avatar initials + name with an optional second line (email, role, …). */
export const PersonCell = ({
  name,
  subtitle,
  badge,
}: {
  name?: string;
  subtitle?: ReactNode;
  badge?: ReactNode;
}) => (
  <Flex align="center" gap={3} minW="180px">
    <Avatar
      size="sm"
      name={(name || "").trim() || "?"}
      bg="#e8f3f3"
      color="#327171"
      fontWeight="700"
    />
    <Box minW={0}>
      <Flex align="center" gap={2}>
        <Text fontSize="13px" fontWeight="600" color="#1d2b2b" m={0} noOfLines={1}>
          {(name || "").trim() || "-"}
        </Text>
        {badge}
      </Flex>
      {subtitle && (
        <Text fontSize="11px" color="gray.500" m={0} noOfLines={1}>
          {subtitle}
        </Text>
      )}
    </Box>
  </Flex>
);

/** Rounded status pill with a consistent colour per status word. */
export const StatusBadge = ({
  status,
  label,
}: {
  status?: string;
  label?: ReactNode;
}) => {
  const key = (status || "").toLowerCase();
  return (
    <Badge
      colorScheme={STATUS_COLOR[key] || "gray"}
      borderRadius="full"
      px={2.5}
      py={0.5}
      fontSize="10px"
    >
      {label ?? (status || "-").replace(/_/g, " ")}
    </Badge>
  );
};

/** Leave type pill with the type's colour dot. */
export const LeaveTypeBadge = ({ type }: { type?: string }) => {
  const meta = LEAVE_TYPE_META[type || ""];
  const color = meta?.color || "gray";
  return (
    <Flex
      display="inline-flex"
      align="center"
      gap={1.5}
      px={2}
      py={0.5}
      borderRadius="full"
      bg={`${color}.50`}
      color={`${color}.700`}
      fontSize="12px"
      fontWeight="600"
      whiteSpace="nowrap"
    >
      <Box w="6px" h="6px" borderRadius="full" bg={`${color}.400`} />
      {meta?.label || type || "-"}
    </Flex>
  );
};
